import React from 'react';
import {
  Box,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  SimpleGrid,
  Icon,
  Badge,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  List,
  ListItem,
  ListIcon,
  Stack,
  Container,
  Progress,
  Image
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaArrowRight, FaCode, FaChartLine, FaRupeeSign, FaUsers, FaGraduationCap } from 'react-icons/fa';
import { FaRocket, FaClock } from 'react-icons/fa';
import heroStudents from '../assets/images/hero-students.jpg';
import AssessmentFlow from "./assessment/AssessmentFlow";

const MotionBox = motion(Box);

// Hero Section Component
export const HeroSection = ({ data, onEnrollClick, onVideoPlay }) => (
  <Box
    minH="90vh"
    bgGradient="linear(to-br, brand.700, brand.900, accent.green)"
    position="relative"
    overflow="hidden"
  >
    <Box
      position="absolute"
      top="10%"
      right="10%"
      w="300px"
      h="300px"
      bg="accent.gold"
      opacity="0.1"
      borderRadius="full"
      filter="blur(80px)"
    />
    <Box
      position="absolute"
      bottom="20%"
      left="5%"
      w="250px"
      h="250px"
      bg="accent.green"
      opacity="0.15"
      borderRadius="full"
      filter="blur(80px)"
    />

    <Container maxW="container.xl" pt={{ base: 16, md: 24 }} pb={20}>
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12} alignItems="center">
        <MotionBox
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Badge
            mb={4}
            px={4}
            py={2}
            bg="accent.gold"
            color="brand.800"
            fontSize="sm"
            fontWeight="700"
            borderRadius="full"
          >
            {data.badge}
          </Badge>
          <Heading
            as="h1"
            fontSize={{ base: '3xl', md: '5xl', lg: '6xl' }}
            fontWeight="900"
            color="white"
            lineHeight="1.1"
            mb={6}
          >
            {data.title}
          </Heading>
          <Text fontSize="xl" color="whiteAlpha.900" mb={8} lineHeight="1.8">
            {data.description}
          </Text>

          <HStack spacing={6} mb={8} flexWrap="wrap">
            {data.trustElements.map((elem, idx) => (
              <HStack key={idx} color="white">
                <Icon as={elem.icon} boxSize={5} color="accent.gold" />
                <Text fontWeight="600">{elem.text}</Text>
              </HStack>
            ))}
          </HStack>

          <Stack direction={{ base: 'column', sm: 'row' }} spacing={4}>
            <AssessmentFlow />
          </Stack>
        </MotionBox>

        <MotionBox
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Box
            bg="whiteAlpha.200"
            borderRadius="2xl"
            backdropFilter="blur(10px)"
            border="1px solid"
            borderColor="whiteAlpha.300"
            display="flex"
            alignItems="center"
            justifyContent="center"
            position="relative"
            cursor="pointer"
            _hover={{ transform: 'scale(1.15)' }}
            transition="all 0.3s"
            onClick={onVideoPlay}
          >
            <Image
        src={heroStudents}
        alt="Students smiling during a hero-students program"
        borderRadius="lg" // Example of a Chakra UI style prop
      />
            {/*<Text color="white" fontSize="lg">Video Testimonial Placeholder</Text>*/}

          </Box>
        </MotionBox>
      </SimpleGrid>
    </Container>
  </Box>
);

// Persona Section Component
export const PersonaSection = ({ data }) => (
  <Box py={{ base: 16, md: 20 }} bg="white">
    <Container maxW="container.xl">
      <VStack spacing={4} mb={12} textAlign="center">
        <Heading size="2xl" fontWeight="900" color="brand.800">
          {data.title}
        </Heading>
        <Text fontSize="lg" color="brand.500" maxW="2xl">
          {data.description}
        </Text>
      </VStack>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={8}>
        {data.cards.map((card, idx) => (
          <PersonaCard key={idx} {...card} delay={idx * 0.1} />
        ))}
      </SimpleGrid>
    </Container>
  </Box>
);

// Persona Card Component
const PersonaCard = ({ icon, title, desc, badge, delay }) => (
  <MotionBox
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay }}
  >
    <Box
      p={8}
      bg="brand.50"
      borderRadius="2xl"
      border="2px solid"
      borderColor="brand.200"
      _hover={{
        transform: 'translateY(-8px)',
        borderColor: 'accent.green',
        boxShadow: '0 12px 40px rgba(16,185,129,0.2)',
      }}
      transition="all 0.3s"
      position="relative"
      h="full"
    >
      {badge && (
        <Badge
          position="absolute"
          top={4}
          right={4}
          bg="accent.gold"
          color="brand.800"
          fontSize="xs"
          px={3}
          py={1}
        >
          {badge}
        </Badge>
      )}
      <Icon as={icon} boxSize={12} color="accent.green" mb={4} />
      <Heading size="md" color="brand.700" mb={3}>
        {title}
      </Heading>
      <Text color="brand.500">{desc}</Text>
    </Box>
  </MotionBox>
);

export const CurriculumSection = ({ data }) => (
  <Box py={{ base: 16, md: 20 }} bg="brand.50">
    <Container maxW="container.xl">
      <VStack spacing={6} mb={12} textAlign="center">
        <Badge
          px={4}
          py={2}
          bg="accent.green"
          color="white"
          fontSize="sm"
          fontWeight="700"
          borderRadius="full"
          textTransform="uppercase"
          letterSpacing="wide"
        >
          Complete Roadmap
        </Badge>
        <Heading 
          size="2xl" 
          fontWeight="900" 
          color="brand.700"
          maxW="4xl"
          lineHeight="1.2"
        >
          {data.title}
        </Heading>
        <Text fontSize="lg" color="brand.600" maxW="2xl" lineHeight="1.8">
          {data.description}
        </Text>
      </VStack>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
        {data.phases.map((phase, idx) => (
          <CurriculumCard key={idx} {...phase} index={idx} />
        ))}
      </SimpleGrid>
    </Container>
  </Box>
);

// Curriculum Card Component
const CurriculumCard = ({ duration, title, desc, projects, index }) => (
  <MotionBox
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
  >
    <Box
      bg="white"
      p={8}
      borderRadius="2xl"
      border="2px solid"
      borderColor="brand.200"
      h="full"
      _hover={{
        borderColor: "accent.green",
        boxShadow: "0 8px 24px rgba(16,185,129,0.15)",
        transform: "translateY(-4px)",
      }}
      transition="all 0.3s"
    >
      <HStack spacing={4} mb={4} align="start">
        <Box
          px={3}
          py={2}
          bg="accent.green"
          borderRadius="lg"
          display="flex"
          alignItems="center"
          justifyContent="center"
          fontWeight="700"
          fontSize="md"
          color="white"
          flexShrink={0}
        >
          {duration}
        </Box>
        

      </HStack>
      
      <Heading size="md" color="brand.700" lineHeight="1.4" mb={3}>
        {title}
      </Heading>
      
      <Text color="brand.500"  mb={4}>
        {desc}
      </Text>
      
      <Badge
        colorScheme="blue"
        fontSize="xs"
        px={3}
        py={1}
        borderRadius="full"
        variant="subtle"
      >
        {projects}
      </Badge>
    </Box>
  </MotionBox>
);


// Project Showcase Component
export const ProjectShowcase = ({ data }) => (
  <Box py={{ base: 16, md: 20 }} bg="white">
    <Container maxW="container.xl">
      <VStack spacing={4} mb={12} textAlign="center">
        <Heading size="2xl" fontWeight="900" color="brand.800">
          {data.title}
        </Heading>
        <Text fontSize="lg" color="brand.500" maxW="2xl">
          {data.description}
        </Text>
      </VStack>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
        {data.list.map((project, idx) => (
          <ProjectCard key={idx} project={project} index={idx} />
        ))}
      </SimpleGrid>
    </Container>
  </Box>
);

// Project Card Component
const ProjectCard = ({ project, index }) => (
  <MotionBox
    initial={{ opacity: 0, scale: 0.9 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true }}
    transition={{ duration: 0.4, delay: index * 0.1 }}
  >
    <Box
      p={6}
      bg="brand.50"
      borderRadius="xl"
      border="1px solid"
      borderColor="brand.200"
      _hover={{
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 30px rgba(16,185,129,0.15)',
      }}
      transition="all 0.3s"
      cursor="pointer"
    >
      <Icon as={FaCode} boxSize={8} color="accent.green" mb={3} />
      <Heading size="sm" color="brand.700" mb={2}>
        {project}
      </Heading>
      <HStack spacing={2} mt={4}>
        <Badge colorScheme="green" variant="subtle">
          GitHub
        </Badge>
        <Badge colorScheme="blue" variant="subtle">
          Live Demo
        </Badge>
      </HStack>
    </Box>
  </MotionBox>
);

// Learning Experience Component
export const LearningExperience = ({ data }) => (
  <Box py={{ base: 16, md: 20 }} bg="brand.50">
    <Container maxW="container.xl">
      <VStack spacing={16}>
        {/* Header Section */}
        <Box textAlign="center" maxW="3xl">
          <Badge mb={4} px={4} py={1} borderRadius="full" fontSize="sm" bg="accent.gold"
              color="brand.800">
            🚀 Accelerated Learning Path
          </Badge>
          <Heading size="2xl" fontWeight="900" color="brand.800" mb={6}>
            {data.title}
          </Heading>
          <Text fontSize="xl" color="brand.500" lineHeight="1.6">
            {data.description}
          </Text>
        </Box>

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12} alignItems="start" w="full">
          {/* Left Column - Weekly Schedule */}
          <Box>
            <VStack spacing={8} align="stretch">
              {/* Weekly Progress */}
              <Box bg="white" p={6} borderRadius="2xl" boxShadow="0 4px 20px rgba(0,0,0,0.08)">
                <HStack justify="space-between" mb={4}>
                  <Text fontWeight="700" color="brand.700">Weekly Progress Tracker</Text>
                  <Badge colorScheme="green" variant="subtle">15-20 hours/week</Badge>
                </HStack>
                <VStack spacing={3} align="stretch">
                  <Box>
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="sm" color="brand.600">Live Sessions</Text>
                      <Text fontSize="sm" fontWeight="600" color="accent.green">3 sessions</Text>
                    </HStack>
                    <Progress value={100} size="sm" colorScheme="green" borderRadius="full" />
                  </Box>
                  <Box>
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="sm" color="brand.600">Practice & Assignments</Text>
                      <Text fontSize="sm" fontWeight="600" color="accent.green">5-7 hours</Text>
                    </HStack>
                    <Progress value={80} size="sm" colorScheme="blue" borderRadius="full" />
                  </Box>
                  <Box>
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="sm" color="brand.600">Support Sessions</Text>
                      <Text fontSize="sm" fontWeight="600" color="accent.green">1 dedicated</Text>
                    </HStack>
                    <Progress value={100} size="sm" colorScheme="purple" borderRadius="full" />
                  </Box>
                </VStack>
              </Box>

              {/* Weekly Schedule */}
              <Box bg="white" p={6} borderRadius="2xl" boxShadow="0 4px 20px rgba(0,0,0,0.08)">
                <Heading size="md" color="brand.700" mb={6} display="flex" alignItems="center" gap={2}>
                  <Icon as={FaChartLine} color="accent.green" /> Weekly Flow
                </Heading>
                <VStack spacing={4} align="stretch">
                  {data.schedule.map((item, idx) => (
                    <HStack 
                      key={idx} 
                      spacing={4} 
                      p={3} 
                      borderRadius="lg"
                      bg={item.type === 'live' ? 'accent.green' : 'transparent'}
                      color={item.type === 'live' ? 'white' : 'inherit'}
                      transition="all 0.3s"
                      _hover={{ transform: 'translateX(4px)', bg: idx === 0 ? 'accent.green' : 'brand.100' }}
                    >
                      <Icon as={item.icon} boxSize={5} />
                      <Box flex="1">
                        <Text fontWeight="700" fontSize="sm">
                          {item.day}
                        </Text>
                        <Text fontSize="sm" opacity={0.9}>
                          {item.activity}
                        </Text>
                      </Box>
                      <Badge 
                        colorScheme={idx === 0 ? 'white' : 'green'} 
                        variant={idx === 0 ? 'solid' : 'subtle'}
                        fontSize="xs"
                      >
                        {item.type }
                      </Badge>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            </VStack>
          </Box>

          {/* Right Column - Expectations & Support */}
          <Box>
            <VStack spacing={8} align="stretch">
              {/* Weekly Expectations */}
              <Box
                bg="white"
                p={8}
                borderRadius="2xl"
                boxShadow="0 4px 20px rgba(0,0,0,0.08)"
                border="2px solid"
                borderColor="accent.green"
                position="relative"
                overflow="hidden"
              >
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  h="4px"
                  bg="accent.green"
                />
                <VStack spacing={6} align="stretch">
                  <Heading size="lg" color="brand.700" display="flex" alignItems="center" gap={2}>
                    <Icon as={FaRocket} color="accent.green" /> {data.expectations.title}
                  </Heading>
                  <List spacing={4}>
                    {data.expectations.items.map((item, idx) => (
                      <ListItem key={idx} color="brand.600" display="flex" alignItems="start">
                        <ListIcon as={FaCheckCircle} color="accent.green" mt={1} />
                        <Box>
                          <Text fontWeight="500">{item}</Text>
                          {idx === 4 && (
                            <Badge colorScheme="green" variant="subtle" mt={1} fontSize="xs">
                              Instant Help
                            </Badge>
                          )}
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </VStack>
              </Box>

              {/* Support System */}
              <Box bg="brand.800" p={6} borderRadius="2xl" color="white">
                <VStack spacing={4} align="stretch">
                  <Heading size="md" display="flex" alignItems="center" gap={2}>
                    <Icon as={FaUsers} color="accent.green" /> Your Support System
                  </Heading>
                  <SimpleGrid columns={2} spacing={4}>
                    <VStack spacing={1} textAlign="center">
                      <Icon as={FaGraduationCap} boxSize={6} color="accent.green" />
                      <Text fontSize="sm" fontWeight="600">5 Online Tests</Text>
                    </VStack>
                    <VStack spacing={1} textAlign="center">
                      <Icon as={FaCode} boxSize={6} color="accent.green" />
                      <Text fontSize="sm" fontWeight="600">Assignments</Text>
                    </VStack>
                    <VStack spacing={1} textAlign="center">
                      <Icon as={FaClock} boxSize={6} color="accent.green" />
                      <Text fontSize="sm" fontWeight="600">1:1 Support</Text>
                    </VStack>
                    <VStack spacing={1} textAlign="center">
                      <Icon as={FaUsers} boxSize={6} color="accent.green" />
                      <Text fontSize="sm" fontWeight="600">24/7 Community</Text>
                    </VStack>
                  </SimpleGrid>
                  <Text fontSize="sm" textAlign="center" opacity={0.9} mt={2}>
                    Complete guidance every step of the way
                  </Text>
                </VStack>
              </Box>
            </VStack>
          </Box>
        </SimpleGrid>
      </VStack>
    </Container>
  </Box>
);



// Success Stories Component
export const SuccessStories = ({ data }) => (
  <Box py={{ base: 16, md: 20 }} bg="white">
    <Container maxW="container.xl">
      <VStack spacing={4} mb={12} textAlign="center">
        <Heading size="2xl" fontWeight="900" color="brand.800">
          {data.title}
        </Heading>
        <Text fontSize="lg" color="brand.500" maxW="2xl">
          {data.description}
        </Text>
      </VStack>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
        {data.stories.map((story, idx) => (
          <SuccessStoryCard key={idx} story={story} index={idx} />
        ))}
      </SimpleGrid>
    </Container>
  </Box>
);

// Success Story Card Component
const SuccessStoryCard = ({ story, index }) => (
  <MotionBox
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay: index * 0.1 }}
  >
    <Box
      p={8}
      bg="brand.50"
      borderRadius="2xl"
      boxShadow="0 4px 20px rgba(0,0,0,0.08)"
      _hover={{ transform: 'translateY(-8px)' }}
      transition="all 0.3s"
      cursor="pointer"
    >
      <Box
        w="100px"
        h="100px"
        bg="brand.200"
        borderRadius="full"
        mx="auto"
        mb={4}
      />
      <Heading size="md" color="brand.700" textAlign="center" mb={2}>
        {story.name}
      </Heading>
      <VStack spacing={2}>
        <Text fontSize="sm" color="brand.500">
          {story.before}
        </Text>
        <Icon as={FaArrowRight} color="accent.green" />
        <Text fontSize="sm" color="accent.green" fontWeight="700">
          {story.after}
        </Text>
      </VStack>
    </Box>
  </MotionBox>
);



// Investment Section Component
export const InvestmentSection = ({ data, onEnrollClick }) => (
  <Box py={{ base: 16, md: 20 }} bg="brand.800">
    <Container maxW="container.xl">
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12} alignItems="center">
        <Box color="white">
          <Heading size="2xl" fontWeight="900" mb={6}>
            {data.leftSide.title}
          </Heading>
          {data.leftSide.subtitle && (
            <Text fontSize="md" mb={4} opacity={0.9} fontWeight="600" color="accent.gold">
              {data.leftSide.subtitle}
            </Text>
          )}
          <Text fontSize="lg" mb={8} opacity={0.9}>
            {data.leftSide.description}
          </Text>

          {/* Value Breakdown (New Structure) or Features (Old Structure) */}
          {data.leftSide.valueBreakdown ? (
            <VStack spacing={6} align="stretch" mb={8}>
              {data.leftSide.totalValue && (
                <Box p={4} bg="whiteAlpha.200" borderRadius="lg" mb={2}>
                  <Text fontSize="sm" opacity={0.8} mb={1}>Total Value</Text>
                  <Text fontSize="3xl" fontWeight="900" color="accent.gold">{data.leftSide.totalValue}</Text>
                </Box>
              )}
              <VStack spacing={3} align="stretch" maxH="400px" overflowY="auto" pr={2}>
                {data.leftSide.valueBreakdown.map((item, idx) => (
                  <Box key={idx} p={3} bg="whiteAlpha.100" borderRadius="lg">
                    <HStack justify="space-between" align="flex-start" mb={1}>
                      <Text fontWeight="700" fontSize="sm">{item.item}</Text>
                      <Text fontWeight="700" fontSize="sm" color="accent.gold" flexShrink={0}>{item.value}</Text>
                    </HStack>
                    <Text fontSize="xs" opacity={0.8}>{item.description}</Text>
                  </Box>
                ))}
              </VStack>
            </VStack>
          ) : data.leftSide.features ? (
            <VStack spacing={4} align="stretch" mb={8}>
              {data.leftSide.features.map((feature, idx) => (
                <HStack key={idx}>
                  <Icon as={FaCheckCircle} color="accent.gold" />
                  <Text>{feature}</Text>
                </HStack>
              ))}
            </VStack>
          ) : null}

          <Text fontSize="sm" opacity={0.8}>
            {data.leftSide.comparison}
          </Text>

          {/* Visual Placeholder */}
          {data.visualPlaceholder && (
            <Box
              mt={6}
              p={4}
              bg="whiteAlpha.100"
              borderRadius="lg"
              borderStyle="dashed"
              borderWidth="2px"
              borderColor="whiteAlpha.300"
              textAlign="center"
            >
              <Text fontSize="xs" opacity={0.8} fontStyle="italic">
                {data.visualPlaceholder}
              </Text>
            </Box>
          )}
        </Box>

        <Box
          bg="white"
          p={10}
          borderRadius="2xl"
          boxShadow="0 12px 40px rgba(0,0,0,0.2)"
          position="relative"
          overflow="hidden"
        >
          {/* Limited Time Offer Badge */}
          <Badge
            mb={4}
            px={4}
            py={2}
            bg="accent.gold"
            color="brand.800"
            fontSize="sm"
            fontWeight="700"
            borderRadius="full"
          >
            {data.pricing.badge}
          </Badge>

          {/* MRP Strike-out and Discounted Price */}
          <HStack spacing={4} align="baseline" mb={2}>
            <Text fontSize="lg" color="brand.400" textDecoration="line-through">
              ₹{data.pricing.mrp}
            </Text>
            <HStack spacing={1}>
              <Icon as={FaRupeeSign} color="accent.green" boxSize={6} />
              <Heading size="3xl" color="brand.800">
                {data.pricing.price}
              </Heading>
            </HStack>
          </HStack>

          {/* Discount Text */}
          <Text fontSize="sm" color="accent.green" fontWeight="600" mb={4}>
            {data.pricing.discountText}
          </Text>

          <Text color="brand.500" mb={6}>
            or ₹{data.pricing.emi}
          </Text>

          {/* Payback Calculator (New Structure) */}
          {data.pricing.paybackCalculator && (
            <Box mb={6} p={4} bg="purple.50" borderRadius="lg">
              <Heading size="sm" color="brand.700" mb={3}>
                {data.pricing.paybackCalculator.title}
              </Heading>
              <VStack spacing={2} align="stretch">
                {data.pricing.paybackCalculator.scenarios?.map((scenario, idx) => (
                  <HStack key={idx} justify="space-between">
                    <Text fontSize="xs" color="brand.600">{scenario.description}</Text>
                    <Text fontSize="xs" fontWeight="700" color="purple.600">{scenario.timeline}</Text>
                  </HStack>
                ))}
              </VStack>
            </Box>
          )}

          {/* What's Included Section */}
          <Box mb={6}>
            <Heading size="md" color="brand.700" mb={4}>
              What's Included:
            </Heading>
            <List spacing={3} maxH="300px" overflowY="auto">
              {data.pricing.included.map((item, idx) => (
                <ListItem key={idx} color="brand.600" fontSize="sm">
                  <HStack spacing={3} align="flex-start">
                    <ListIcon as={FaCheckCircle} color="accent.green" mt={1} />
                    <Text>{item}</Text>
                  </HStack>
                </ListItem>
              ))}
            </List>
          </Box>

          <Button
            as="a"
            href={data.pricing.ctaHref}
            variant="solid"
            size="lg"
            w="full"
            h="60px"
            mb={4}
            bg="accent.green"
            color="white"
            boxShadow="0 8px 30px rgba(16,185,129,0.4)"
            _hover={{
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 40px rgba(16,185,129,0.5)',
            }}
            onClick={onEnrollClick}
          >
            Enroll Now
          </Button>

          <HStack justify="center" spacing={4} color="brand.500" fontSize="sm">
            <HStack>
              <Icon as={FaCheckCircle} color="accent.green" />
              <Text>{data.pricing.guarantee}</Text>
            </HStack>
          </HStack>
        </Box>
      </SimpleGrid>
    </Container>
  </Box>
);

// Risk Reversal Component
export const RiskReversal = ({ data }) => (
  <Box py={{ base: 16, md: 20 }} bg="accent.green">
    <Container maxW="container.xl">
      <VStack spacing={6} textAlign="center" color="white">
        <Icon as={data.icon} boxSize={16} color="accent.gold" />
        <Heading size="2xl" fontWeight="900">
          {data.title}
        </Heading>
        <Text fontSize="lg" maxW="2xl" opacity={0.95}>
          {data.description}
        </Text>
        <HStack spacing={8} flexWrap="wrap" justify="center" pt={4}>
          {data.stats.map((stat, idx) => (
            <VStack key={idx}>
              <Text fontWeight="700" fontSize="2xl">
                {stat.value}
              </Text>
              <Text fontSize="sm" opacity={0.9}>
                {stat.label}
              </Text>
            </VStack>
          ))}
        </HStack>
      </VStack>
    </Container>
  </Box>
);

// Urgency Section Component
export const UrgencySection = ({ data }) => (
  <Box py={{ base: 12, md: 16 }} bg="brand.50">
    <Container maxW="container.xl">
      <VStack spacing={6} textAlign="center">
        <Badge
          px={6}
          py={3}
          bg="accent.gold"
          color="brand.800"
          fontSize="lg"
          fontWeight="700"
          borderRadius="full"
        >
          {data.badge}
        </Badge>
        <Heading size="xl" color="brand.800">
          {data.title}
        </Heading>
      </VStack>
    </Container>
  </Box>
);

// FAQ Section Component
export const FAQSection = ({ data }) => (
  <Box py={{ base: 16, md: 20 }} bg="white">
    <Container maxW="container.xl">
      <VStack spacing={4} mb={12} textAlign="center">
        <Heading size="2xl" fontWeight="900" color="brand.800">
          {data.title}
        </Heading>
        <Text fontSize="lg" color="brand.500" maxW="2xl">
          {data.description}
        </Text>
      </VStack>

      <Accordion allowToggle maxW="3xl" mx="auto">
        {data.items.map((faq, idx) => (
          <FAQItem key={idx} question={faq.q} answer={faq.a} index={idx} />
        ))}
      </Accordion>
    </Container>
  </Box>
);

// FAQ Item Component
const FAQItem = ({ question, answer, index }) => (
  <AccordionItem key={index} border="none" mb={4}>
    <AccordionButton
      p={6}
      bg="brand.50"
      borderRadius="xl"
      _hover={{ bg: 'brand.100' }}
      _expanded={{ bg: 'accent.green', color: 'white' }}
    >
      <Box flex="1" textAlign="left">
        <Text fontWeight="700" fontSize="lg">
          {question}
        </Text>
      </Box>
      <AccordionIcon />
    </AccordionButton>
    <AccordionPanel p={6} bg="brand.50" borderRadius="xl" mt={2}>
      <Text color="brand.600">{answer}</Text>
    </AccordionPanel>
  </AccordionItem>
);

// Final CTA Component
export const FinalCTA = ({ data, onEnrollClick }) => (
  <Box py={{ base: 16, md: 20 }} bg="blue.900">
    <Container maxW="container.xl">
      <VStack spacing={8} textAlign="center" color="white">
        <Heading size="2xl" fontWeight="900">
          {data.title}
        </Heading>
        <Text fontSize="xl" maxW="2xl" opacity={0.9}>
          {data.description}
        </Text>

        <Stack direction={{ base: 'column', sm: 'row' }} spacing={4}>
          {data.buttons.map((button, idx) => (
            <Button
              key={idx}
              as="a"
              href={button.href}
              variant={button.variant}
              size="lg"
              h="60px"
              px={10}
              boxShadow={button.variant === 'solid' ? '0 8px 30px rgba(16,185,129,0.4)' : 'none'}
              borderColor={button.variant === 'outline' ? 'white' : undefined}
              color={button.variant === 'outline' ? 'white' : undefined}
              rightIcon={button.variant === 'solid' ? <FaArrowRight /> : undefined}
              onClick={button.variant === 'solid' ? onEnrollClick : undefined}
            >
              {button.text}
            </Button>
          ))}
        </Stack>

        <Text fontSize="sm" opacity={0.8} maxW="md">
          {data.footer}
        </Text>
      </VStack>
    </Container>
  </Box>
);